import React, { useState, useRef, useEffect } from "react";
import { HiMenuAlt4, HiX, HiChevronDown } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { images } from "../../constants";

import usaFlag from "./united-states-of-america-united-states-svgrepo-com.svg";
import franceFlag from "./france-svgrepo-com.svg";
import spainFlag from "./spain-svgrepo-com.svg";

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "service", label: "Services" },
  { id: "ventures", label: "Ventures" },
  { id: "investor", label: "Investors" },
  { id: "work", label: "Portfolio" },
  { id: "team", label: "Team" },
  { id: "internship", label: "Internships" },
  { id: "blog", label: "Insights" },
  { id: "store", label: "Boutique" },
  { id: "contact", label: "Contact" },
];

const LANGUAGES = [
  { code: "en", label: "English", flag: usaFlag },
  { code: "fr", label: "Français", flag: franceFlag },
  { code: "es", label: "Español", flag: spainFlag },
];

const Navbar = () => {
  const [toggle, setToggle] = useState(false);
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    const visibleSections = {};

    NAV_ITEMS.forEach((item) => {
      const el = document.getElementById(item.id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          visibleSections[item.id] = entry.isIntersecting;

          const firstVisibleId = NAV_ITEMS.find(
            (navItem) => visibleSections[navItem.id]
          )?.id;
          if (firstVisibleId) setActiveSection(firstVisibleId);
        },
        { threshold: [0, 0.25, 0.5] }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock the page while the mobile drawer is open.
  useEffect(() => {
    if (!toggle) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setToggle(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [toggle]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsDropdownOpen(false);
  };

  const activeLanguage =
    LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <nav
      aria-label={t("Primary")}
      className={`fixed w-full z-[1000] transition-all duration-300 ${
        scrolled
          ? "py-3 bg-paper/90 backdrop-blur-md border-b border-[color:var(--rule)]"
          : "py-5 bg-transparent border-b border-transparent"
      }`}
      style={{ top: "var(--banner-height, 0px)" }}
    >
      {/* Reading progress — a hairline, not a glowing bar. */}
      <div
        aria-hidden
        className="absolute bottom-[-1px] left-0 h-px bg-gold origin-left transition-transform duration-150"
        style={{ width: "100%", transform: `scaleX(${scrollProgress / 100})` }}
      />

      <div className="shell flex items-center justify-between gap-6">
        <a
          href="#home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="shrink-0"
          aria-label="Hyphen Tech — home"
        >
          <img
            src={images.hyphenlogo}
            alt="Hyphen Tech"
            className={`w-auto transition-all duration-300 ${scrolled ? "h-7" : "h-9"}`}
          />
        </a>

        {/* ── Desktop ── */}
        <ul className="hidden xl:flex items-center gap-7">
          {NAV_ITEMS.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative block text-[13px] font-medium py-1 transition-colors duration-200 ${
                    isActive ? "text-ink" : "text-ink-soft hover:text-ink"
                  }`}
                >
                  {t(item.label)}
                  {isActive && (
                    <motion.span
                      layoutId="navRule"
                      className="absolute -bottom-0.5 left-0 right-0 h-px bg-gold"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-2">
          {/* ── Language ── */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
              className="flex items-center gap-2 h-11 px-3 text-ink-soft transition-colors hover:text-ink"
            >
              <img
                src={activeLanguage.flag}
                alt=""
                aria-hidden
                className="w-4 h-3 object-cover"
              />
              <span className="label">{i18n.language}</span>
              <HiChevronDown
                aria-hidden
                className={`text-sm transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {isDropdownOpen && (
                <motion.ul
                  role="listbox"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-2 min-w-[10rem] bg-paper border border-[color:var(--rule-strong)] shadow-lg p-1 z-50"
                >
                  {LANGUAGES.map((lang) => (
                    <li key={lang.code}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={i18n.language === lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                          i18n.language === lang.code
                            ? "text-ink font-semibold bg-paper-warm"
                            : "text-ink-soft hover:bg-paper-warm hover:text-ink"
                        }`}
                      >
                        <img src={lang.flag} alt="" aria-hidden className="w-4 h-3 object-cover" />
                        {lang.label}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          {/* ── Mobile trigger ── */}
          <button
            type="button"
            onClick={() => setToggle(true)}
            aria-label={t("Open menu")}
            aria-expanded={toggle}
            className="xl:hidden w-11 h-11 flex items-center justify-center text-ink transition-colors hover:text-ink-soft"
          >
            <HiMenuAlt4 aria-hidden className="text-xl" />
          </button>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      <AnimatePresence>
        {toggle && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setToggle(false)}
              className="fixed inset-0 bg-ink/50 z-[1001]"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              className="fixed top-0 right-0 h-full w-[86%] max-w-sm bg-paper z-[1002] border-l border-[color:var(--rule)] flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--rule)]">
                <img src={images.hyphenlogo} alt="Hyphen Tech" className="h-7 w-auto" />
                <button
                  type="button"
                  onClick={() => setToggle(false)}
                  aria-label={t("Close menu")}
                  className="w-11 h-11 flex items-center justify-center text-ink"
                >
                  <HiX aria-hidden className="text-xl" />
                </button>
              </div>

              <ul className="flex-1 overflow-y-auto px-6">
                {NAV_ITEMS.map((item, idx) => {
                  const isActive = activeSection === item.id;
                  return (
                    <li key={item.id} className="border-b border-[color:var(--rule)]">
                      <motion.a
                        href={`#${item.id}`}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => setToggle(false)}
                        className="flex items-center justify-between py-4"
                      >
                        <span
                          className={`font-heading text-xl ${
                            isActive ? "text-ink" : "text-ink-soft"
                          }`}
                        >
                          {t(item.label)}
                        </span>
                        <span className="label tabular text-ink-faint">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                      </motion.a>
                    </li>
                  );
                })}
              </ul>

              <div className="px-6 py-6 border-t border-[color:var(--rule)]">
                <a href="mailto:hyphentechinfo@gmail.com" className="btn btn-solid w-full">
                  {t("Get In Touch")}
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
